// The Documents list's sorting, filters, tabs and wording, apart from the page so tests can load
// them without MSAL.

import { INCLUSION_LABELS, inclusionOf, isArchived, type HrDocument, type Inclusion, type ReviewStatus } from "@ask-hr/core/documents";
import { APPLICABILITY_LABELS, type SharePointSource } from "@ask-hr/core/sources";
import { VISIBILITY_ORDER, isHidden } from "@ask-hr/core/visibility";
import type { ApiError } from "../response.js";
import { sortRows, type Sort, type SortValues } from "./sort.js";
import { label } from "./sources.js";

export type Doc = HrDocument & { status: ReviewStatus };
export type DocSortKey = "document" | "source" | "applies" | "included" | "status" | "employees" | "modified";
export const DOC_DESC_FIRST: readonly DocSortKey[] = ["modified"];

// Status sorts in working order, what needs HR first, rather than by its label.
const STATUS_ORDER: Record<ReviewStatus, number> = { new: 0, changed: 1, error: 2, approved: 3, excluded: 4, removed: 5 };

// Where a document lives: its library, folder and name. The list's order until a header is clicked.
export const docPath = (d: Doc) => `${d.driveName}${d.path}/${d.name}`;

export function sortDocs(docs: Doc[], sort: Sort<DocSortKey>, sourceOf: Map<string, SharePointSource>): Doc[] {
  const values: SortValues<Doc, DocSortKey> = {
    document: (d) => d.name,
    // The source's name as Sources shows it; source removed: "".
    source: (d) => {
      const s = sourceOf.get(d.sourceId);
      return s ? label(s) : "";
    },
    // What the column shows: the document's own setting, else its source's. Source removed: "".
    applies: (d) => {
      const who = d.applicability ?? sourceOf.get(d.sourceId)?.applicability;
      return who ? (APPLICABILITY_LABELS[who] ?? who) : "";
    },
    included: (d) => INCLUSION_LABELS[inclusionOf(d)],
    status: (d) => STATUS_ORDER[d.status],
    // Fewest people first; not checked yet goes last.
    employees: (d) => (d.visibility ? VISIBILITY_ORDER[d.visibility.state] : 9),
    modified: (d) => d.modified ?? "",
  };
  return sortRows(docs, sort, values, docPath);
}

// ---- the toolbar's column filters, and shift-click selection

// "" in a field means any. applies is the value the column shows (the document's own setting,
// else its source's); permissions is a state, "hidden" (most employees can't open it: some
// people, or an organization link only) or "unchecked" (not checked yet).
export interface DocFilters {
  source: string;
  applies: string;
  included: string;
  permissions: string;
}
export const NO_FILTERS: DocFilters = { source: "", applies: "", included: "", permissions: "" };

export const effectiveApplies = (d: Doc, sourceOf: Map<string, SharePointSource>) => d.applicability ?? sourceOf.get(d.sourceId)?.applicability ?? "";
export const permissionsOf = (d: Doc) => d.visibility?.state ?? "unchecked";

export function filterDocs(docs: Doc[], f: DocFilters, sourceOf: Map<string, SharePointSource>): Doc[] {
  return docs.filter(
    (d) =>
      (!f.source || d.sourceId === f.source) &&
      (!f.applies || effectiveApplies(d, sourceOf) === f.applies) &&
      (!f.included || inclusionOf(d) === f.included) &&
      (!f.permissions || (f.permissions === "hidden" ? isHidden(d.visibility) : permissionsOf(d) === f.permissions)),
  );
}

// Shift-click: every row from the last one clicked to this one, in the order shown, takes the
// clicked row's new state. No anchor, or it's no longer shown: just this row.
export function rangeSelect(selected: ReadonlySet<string>, shownIds: readonly string[], anchor: string | null, id: string): Set<string> {
  const next = new Set(selected);
  const on = !selected.has(id);
  const a = anchor === null ? -1 : shownIds.indexOf(anchor);
  const b = shownIds.indexOf(id);
  const ids = a < 0 || b < 0 ? [id] : shownIds.slice(Math.min(a, b), Math.max(a, b) + 1);
  for (const x of ids) {
    if (on) next.add(x);
    else next.delete(x);
  }
  return next;
}

// ---- grouping by source

export interface SourceGroup {
  key: string; // the source's id
  name: string;
  src: SharePointSource | undefined; // undefined: the source was removed
  docs: Doc[];
}

// The rows under each source, in the order given; sources by name, a removed one last.
export function groupBySource(docs: readonly Doc[], sourceOf: Map<string, SharePointSource>): SourceGroup[] {
  const by = new Map<string, Doc[]>();
  for (const d of docs) {
    const list = by.get(d.sourceId);
    if (list) list.push(d);
    else by.set(d.sourceId, [d]);
  }
  return [...by]
    .map(([key, list]) => {
      const src = sourceOf.get(key);
      return { key, name: src ? label(src) : "Removed source", src, docs: list };
    })
    .sort((a, b) => Number(!a.src) - Number(!b.src) || a.name.localeCompare(b.name));
}

// ---- the page's tabs, flags and wording

// Documents in Archive folders only show under Archive, unless HR approved one: then it is part of
// what Ask HR answers from, and shows with the other approved documents too.
// Three tabs; the rest are views chosen under Filters ("Also show").
export type Filter = "review" | "approved" | "all" | "problems" | "excluded" | "archive" | "removed";
export const TABS: readonly Filter[] = ["review", "approved", "all"];
export const MORE: readonly (readonly [Filter, string])[] = [
  ["problems", "Problems"],
  ["excluded", "Excluded"],
  ["archive", "Archive"],
  ["removed", "Removed"],
];
const inMain = (d: Doc) => !isArchived(d) || d.status === "approved";
export const FILTERS: Record<Filter, (d: Doc) => boolean> = {
  review: (d) => inMain(d) && (d.status === "new" || d.status === "changed"),
  approved: (d) => d.status === "approved",
  all: (d) => inMain(d) && d.status !== "removed",
  problems: (d) => inMain(d) && d.status === "error",
  excluded: (d) => inMain(d) && d.status === "excluded",
  archive: (d) => isArchived(d) && d.status !== "removed",
  removed: (d) => d.status === "removed",
};

export const PILL: Record<ReviewStatus, string> = {
  new: "pill alert",
  changed: "pill alert",
  approved: "pill",
  excluded: "pill ok",
  removed: "pill ok",
  error: "pill bad",
};

const ERRORS: Record<string, string> = {
  no_text: "No text found. It may be a scanned image; save it as a text PDF or a Word file.",
  too_large: "The file is over 40 MB.",
  unreadable: "The file couldn't be opened. It may be damaged or password-protected.",
  unsupported: "Only PDF and Word (.docx) files can be read.",
  scan_too_long: "A scanned PDF over 30 pages or 32 MB is too long to read from its images. Save it as a text PDF or a Word file, or split it.",
  scan_incomplete: "This scanned PDF has more text than can be read from its images at once. Save it as a text PDF or a Word file, or split it.",
  scan_empty: "No text found, even reading the page images.",
  scan_budget: "This scanned PDF will be read from its images once Ask HR's monthly AI budget resets.",
  scan_later: "A scanned PDF waiting its turn to be read from its images. The next sync reads it.",
  web_too_large: "The page is over 5 MB.",
  web_type: "The address didn't return a readable web page.",
  web_blocked: "The page redirects to an address Ask HR won't fetch.",
  web_unreachable: "The website couldn't be reached. The next sync will try again.",
};
// What went wrong reading a document, for HR.
export const errorText = (code?: string) =>
  code && ERRORS[code]
    ? ERRORS[code]
    : code?.startsWith("graph_")
      ? "SharePoint didn't return the file. The next sync will try again."
      : code?.startsWith("web_")
        ? "The website returned an error. The next sync will try again."
        : "Something went wrong reading this file. The next sync will try again.";

// The folder a document is in below its source (the Source column names the source itself), or
// "" at the source's top. Source removed: the library and folder, as before.
export function folderInSource(d: Doc, src: Pick<SharePointSource, "path"> | undefined): string {
  if (!src) return `${d.driveName}${d.path === "/" ? "" : d.path}`;
  if (src.path === "/") return d.path === "/" ? "" : d.path;
  return d.path.startsWith(`${src.path}/`) ? d.path.slice(src.path.length) : "";
}

export const when = (iso?: string) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
export const day = (iso?: string) => (iso ? new Date(iso).toLocaleDateString() : "");
export const k = (n: number) => `${Math.round(n / 1000)}k`;
export const INCLUSIONS = Object.entries(INCLUSION_LABELS) as [Inclusion, string][];
export const ADMIN_ONLY = "Only Ask HR admins can change this.";

export type PermissionFilter = "hidden" | "everyone" | "unknown" | "unchecked";
export const PERMISSION_FILTER_LABELS: Record<PermissionFilter, string> = {
  hidden: "Employees can't open",
  everyone: "Everyone",
  unknown: "Can't tell",
  unchecked: "Not checked yet",
};

// Filters a link brought in the address (Sources links here): ?source=…&tab=all&permissions=hidden.
export function linkedFilters(search: string): { tab: Filter; filters: DocFilters } {
  const q = new URLSearchParams(search);
  const tab = q.get("tab");
  return {
    tab: tab === "all" || tab === "approved" ? tab : "review",
    filters: { ...NO_FILTERS, source: q.get("source") ?? "", permissions: q.get("permissions") === "hidden" ? "hidden" : "" },
  };
}

// Whether rows are grouped by source: on unless this browser turned it off.
export const GROUPED_KEY = "askhr.documents.grouped";
export function readGrouped(storage: Pick<Storage, "getItem"> | undefined): boolean {
  try {
    return storage?.getItem(GROUPED_KEY) !== "0";
  } catch {
    return true;
  }
}

// What an API error means on this page; undefined for the general line (components/ErrorNotice).
export function explainDocumentsError(e: ApiError): string | undefined {
  if (e.status === 403) return /HR\.Admin/.test(String(e.body.detail ?? "")) ? ADMIN_ONLY : "You need the HR.Manager or HR.Admin role for this.";
  if (e.body.error === "missing_text") return "Some approved documents are missing their text. Run a sync, then publish again.";
  if (e.body.error === "publish_failed") return "Publishing failed. Nothing changed for employees. Try again.";
  if (e.body.error === "index_failed") return "Publishing failed preparing the Relevant parts documents for search. Nothing changed for employees. Try again.";
  return undefined;
}
